import { createStudioUploadQueue } from './studioUploadQueue';
import { API_BASE_URL, API_ENDPOINTS } from "../config";
import { auth, storage } from "../firebaseClient";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { getRuntimeE2EToken } from "./mediaAuth";
import { uploadMulticamSourceResumable } from "./multicamResumableUpload";
import { hashStudioSourceBlob } from "./studioSourceHash";

const backendUploadQueue=createStudioUploadQueue();

export const STORAGE_UPLOAD_LIMIT_MB = 500;
const TEMPORARY_SOURCE_PREFIXES = Object.freeze({
  viral_scan: "temp_scans",
});

export function inferUploadMediaType(file, fallback = "video") {
  const inferred = file && typeof file.type === "string" ? file.type.split("/")[0] : "";
  return inferred || fallback;
}

function buildStructuredUploadError(result, fallbackMessage, httpStatus) {
  const serverErr =
    (result && (result.error || result.message || result.text)) ||
    fallbackMessage ||
    "Upload failed";
  const enrichedError = new Error(serverErr);
  if (result && typeof result === "object") {
    enrichedError.code = result.code;
    enrichedError.context = result.context || null;
    enrichedError.upgradeRequired = result.upgrade_required === true;
  }
  if (httpStatus) enrichedError.httpStatus = httpStatus;
  return enrichedError;
}

export function buildBackendUploadError(error) {
  if (error?.httpStatus || error?.code) return error;
  if (error instanceof TypeError) {
    return new Error("Upload request could not reach the backend. Check the API server and retry.");
  }
  return new Error(error?.message || "Upload failed. Please try again.");
}

function sanitizeStorageFileName(fileName) {
  const raw = String(fileName || "untitled").trim() || "untitled";
  return Array.from(raw)
    .map(char => {
      const code = char.charCodeAt(0);
      const isAlphaNumeric =
        (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
      return isAlphaNumeric || char === "." || char === "_" || char === "-" ? char : "_";
    })
    .join("")
    .slice(0, 120);
}

export async function uploadTemporaryVideoSource({ file, purpose, onProgress }) {
  const user = auth.currentUser;
  if (!user?.uid) {
    throw new Error("Please sign in again before uploading.");
  }
  if (!(file instanceof Blob)) {
    throw new Error("Upload requires a File or Blob.");
  }
  const prefix = TEMPORARY_SOURCE_PREFIXES[purpose];
  if (!prefix) {
    throw new Error("Invalid temporary upload purpose.");
  }
  if (file.size <= 0 || file.size > STORAGE_UPLOAD_LIMIT_MB * 1024 * 1024) {
    throw new Error(`Video uploads must be smaller than ${STORAGE_UPLOAD_LIMIT_MB} MB.`);
  }
  if (file.type && !file.type.startsWith("video/") && file.type !== "application/octet-stream") {
    throw new Error("The selected source must be a video.");
  }

  const safeFileName = sanitizeStorageFileName(file.name || "source-video");
  const storagePath = `${prefix}/${user.uid}/${Date.now()}_${safeFileName}`;
  const fileRef = ref(storage, storagePath);

  const snapshot = await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file, {
      contentType: file.type || "application/octet-stream",
      customMetadata: {
        ownerUid: user.uid,
        source: "autopromote_secure_temporary_upload",
        sourcePurpose: purpose,
      },
    });

    task.on(
      "state_changed",
      state => {
        if (typeof onProgress === "function") {
          onProgress(state.bytesTransferred, state.totalBytes || file.size || 0);
        }
      },
      reject,
      () => resolve(task.snapshot)
    );
  });

  const size = snapshot.metadata?.size ? Number(snapshot.metadata.size) : file.size;
  return {
    ok: true,
    storagePath,
    size,
    uploadMode: "firebase_resumable_temporary",
  };
}

async function uploadSourceFileViaFirebase({
  file,
  mediaType,
  fileName,
  onProgress,
  timeoutMs,
  contentHash,
}) {
  const user = auth.currentUser;
  if (!user?.uid) {
    throw new Error("Please sign in again before uploading.");
  }

  const safeMediaType = ["video", "image", "audio"].includes(mediaType) ? mediaType : inferUploadMediaType(file);
  const safeFileName = sanitizeStorageFileName(fileName || file.name || "untitled");
  const storagePath = `uploads/${safeMediaType}s/${user.uid}/${Date.now()}_${safeFileName}`;
  const fileRef = ref(storage, storagePath);

  const snapshot = await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file, {
      contentType: file.type || "application/octet-stream",
      customMetadata: {
        ownerUid: user.uid,
        source: "unified_publisher_client_upload",
        ...(contentHash ? { contentHash } : {}),
      },
    });

    let settled = false;
    const finish = callback => value => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      callback(value);
    };
    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      if (typeof task.cancel === "function") task.cancel();
      reject(new Error("Media upload timed out. Check your connection and retry."));
    }, timeoutMs);

    task.on(
      "state_changed",
      state => {
        if (typeof onProgress === "function") {
          onProgress(state.bytesTransferred, state.totalBytes || file.size || 0);
        }
      },
      finish(reject),
      () => finish(resolve)(task.snapshot)
    );
  });

  const url = await getDownloadURL(snapshot.ref);
  const size = snapshot.metadata?.size ? Number(snapshot.metadata.size) : file.size || 0;
  return {
    ok: true,
    storagePath,
    url,
    size,
    uploadMode: "firebase_resumable",
  };
}

async function uploadSourceFileViaBackendRequestRaw({
  file,
  token,
  mediaType,
  fileName,
  onProgress,
  timeoutMs,
}) {
  if (!(file instanceof Blob)) {
    throw new Error("Upload requires a File or Blob.");
  }

  if (typeof onProgress === "function") {
    onProgress(0, file.size || 0);
  }

  const uploadUrl = new URL(API_ENDPOINTS.CONTENT_SOURCE_UPLOAD);
  uploadUrl.searchParams.set("mediaType", mediaType || inferUploadMediaType(file));
  uploadUrl.searchParams.set("fileName", fileName || file.name || "untitled");

  let response;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    response = await fetch(uploadUrl.toString(), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
      signal: controller.signal,
    });
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error("Media upload timed out. Check your connection and retry.");
    }
    throw buildBackendUploadError(error);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response || typeof response.ok !== "boolean") {
    throw new Error("Upload service returned no HTTP response.");
  }

  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.url) {
    const error=buildStructuredUploadError(result, "Failed to upload source media", response.status);
    const delay=Number(response.headers?.get?.('Retry-After'));
    if(response.status===429&&Number.isFinite(delay)&&delay>0&&delay<=60)error.retryAfterMs=delay*1000;
    throw error;
  }

  if (typeof onProgress === "function") {
    const size = file.size || result.size || 0;
    onProgress(size, size);
  }

  return result;
}

const uploadSourceFileViaBackendRequest=options=>backendUploadQueue(()=>uploadSourceFileViaBackendRequestRaw(options));

export async function uploadSourceFileViaBackend({
  file,
  token,
  getToken,
  mediaType,
  fileName,
  onProgress,
  timeoutMs = 180000,
  purpose,
}) {
  if (!(file instanceof Blob)) {
    throw new Error("Upload requires a File or Blob.");
  }

  const normalizedMediaType = mediaType || inferUploadMediaType(file);
  const audioOwnerUid = normalizedMediaType === 'audio' && !getRuntimeE2EToken()
    ? auth.currentUser?.uid : null;
  const assertAudioOwner = () => {
    if (normalizedMediaType === 'audio' && !getRuntimeE2EToken() &&
        (!audioOwnerUid || auth.currentUser?.uid !== audioOwnerUid)) {
      throw new Error('Your sign-in changed during the audio upload. Sign in again and retry.');
    }
  };
  assertAudioOwner();
  const contentHash = normalizedMediaType === 'audio' ? await hashStudioSourceBlob(file) : null;
  assertAudioOwner();
  const bindAudioSource = result => {
    assertAudioOwner();
    if (!contentHash) return result;
    if (result.contentHash && result.contentHash !== contentHash)
      throw new Error('Uploaded audio does not match the selected source bytes.');
    return { ...result, contentHash, ...(audioOwnerUid ? { ownerUid: audioOwnerUid } : {}) };
  };
  // Small Studio videos need the same durable owner/purpose identity as large
  // recordings. General Firebase uploads cannot satisfy model source binding.
  if (["studio_source", "studio_project"].includes(purpose) && normalizedMediaType === 'video') {
    const uploaded = await uploadMulticamSourceResumable({
      apiBaseUrl: API_BASE_URL,
      token,
      getToken,
      file,
      purpose,
      onProgress,
    });
    return { ...uploaded, uploadMode: "studio_resumable" };
  }
  if (getRuntimeE2EToken()) {
    return bindAudioSource(await uploadSourceFileViaBackendRequest({
      file,
      token: token || getRuntimeE2EToken(),
      mediaType: normalizedMediaType,
      fileName,
      onProgress,
      timeoutMs,
    }));
  }
  try {
    return bindAudioSource(await uploadSourceFileViaFirebase({
      file,
      mediaType: normalizedMediaType,
      fileName,
      onProgress,
      timeoutMs,
      contentHash,
    }));
  } catch (firebaseError) {
    assertAudioOwner();
    console.warn("Firebase resumable upload failed, falling back to backend upload:", firebaseError);
    return bindAudioSource(await uploadSourceFileViaBackendRequest({
      file,
      token,
      mediaType: normalizedMediaType,
      fileName,
      onProgress,
      timeoutMs,
    }));
  }
}
